import {
	formatDate,
	getDiff,
	isAfter,
	isBefore,
	isSame,
	startOf,
} from "@sembark-travel/datetime-utils"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import {
	AsyncSelect,
	type AsyncSelectProps,
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Icons,
	Inline,
	Money,
	MoneySum,
	Select,
	SROnly,
	Stack,
	Table,
	TableFooterDataCell,
	Text,
	useTimeout,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	EmptyNumberValidator,
	FieldArray,
	FieldGroup,
	Form,
	FormProps,
	GetFieldValue,
	SelectField,
	isTruthy,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	useForm,
	useFormState,
	validateFormValues,
} from "@sembark-travel/ui-form"
import {
	addMoneyFromDifferentCurrencies,
	formatMoneyByDecimal,
	moneyParseByDecimal,
	multipleMoneyBy,
	TMoney,
} from "@sembark-travel/money"
import { isAbortError, useXHR } from "@sembark-travel/xhr"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as Validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "./../Auth"
import { IHotel } from "./../Hotels"
import { IDaywiseHotelAddonPrice } from "./../utils"
import { getHotelAddonDatewisePrices, getHotelAddonNightPrice } from "./utils"
import { hotelAddOnsXHR, THotelAddon } from "../HotelAddOns"
import { hotelAddOnsPricesXHR } from "../HotelAddOnsPrices"

export interface IHotelAddonsParams {
	hotel_addons?: Array<{
		hotel?: IHotel
		addon?: THotelAddon
		dates: Array<Date>
		quantity?: number
		comments?: string
		fetching_prices?: 0 | 1
		date_wise_prices?: Array<IDaywiseHotelAddonPrice>
		// UI only: nights auto-removed as they left the hotel's stay, for the notice
		removed_dates?: Array<Date>
	}>
}

type THotelAddonRow = Required<IHotelAddonsParams>["hotel_addons"][number]

type TOptionHotel = {
	hotel?: IHotel
	dates?: Array<Date>
}

type TNightOption = { id: string; name: string; value: Date }

const validationSchema = Validator.object().shape({
	hotel_addons: Validator.array().of(
		Validator.object().shape({
			hotel: Validator.object().required("Please select a hotel."),
			addon: Validator.object()
				.typeError("Please select an extra service.")
				.required("Please select an extra service."),
			dates: Validator.array().min(1, "Please select at least one night."),
			quantity: EmptyNumberValidator()
				.required("Please enter a quantity.")
				.integer("Quantity should be a whole number.")
				.positive("Quantity should be at least 1."),
			comments: Validator.string(),
		})
	),
})

function getAddonHotelOptions(hotels: Array<TOptionHotel>): Array<IHotel> {
	const byId = new Map<number, IHotel>()
	hotels.forEach(({ hotel }) => {
		if (hotel && !byId.has(hotel.id)) byId.set(hotel.id, hotel)
	})
	return Array.from(byId.values())
}

// Union of a hotel's nights across every row in the option (same hotel can repeat)
function getStayNightsForHotel(
	hotels: Array<TOptionHotel>,
	hotelId: number | undefined,
	bookingFrom: string
): Array<TNightOption> {
	if (hotelId === undefined) return []
	const byDay = new Map<string, Date>()
	hotels.forEach(({ hotel, dates }) => {
		if (hotel?.id !== hotelId) return
		dates?.forEach((date) => {
			const night = startOf(date, "day")
			byDay.set(formatDate(night, "YYYY-MM-DD"), night)
		})
	})
	return Array.from(byDay.entries())
		.sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0))
		.map(([id, value]) => ({
			id,
			value,
			name: `${withOrdinalSuffix(
				getDiff(value, startOf(bookingFrom, "day"), "days") + 1
			)} N (${formatDate(value, "ddd DD MMM")})`,
		}))
}

function isCompleteRow(row: THotelAddonRow) {
	return Boolean(
		row.hotel && row.addon && row.dates?.length && Number(row.quantity) > 0
	)
}

export function HotelAddonsForm({
	initialValues: initialValuesProp,
	hotels,
	bookingFrom,
	onChange,
}: {
	initialValues?: IHotelAddonsParams
	hotels: Array<TOptionHotel>
	bookingFrom: string
	onChange: (addons: Array<THotelAddonRow>) => void
}) {
	const [initialValues] = useState<IHotelAddonsParams>(
		() => initialValuesProp || { hotel_addons: [] }
	)
	const hotelOptions = useMemo(() => getAddonHotelOptions(hotels), [hotels])

	const notifyOnChange = useCallback(
		(values: IHotelAddonsParams) => {
			onChange((values.hotel_addons || []).filter(isCompleteRow))
		},
		[onChange]
	)
	const onSubmit: FormProps<IHotelAddonsParams>["onSubmit"] = useCallback(
		(values) => notifyOnChange(values),
		[notifyOnChange]
	)

	return (
		<Form<IHotelAddonsParams>
			initialValues={initialValues}
			validate={validateFormValues(validationSchema)}
			onSubmit={onSubmit}
			subscription={{}}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<FieldArray<THotelAddonRow> name="hotel_addons">
						{({ fields }) => (
							<Stack gap="4">
								{fields.map((name, index) => (
									<Box key={name} borderBottomWidth="4" borderColor="default">
										<HotelAddonPricesReset name={name} />
										<RatesForHotelAddon name={name} />
										<HotelAddonGivenPriceSync name={name} />

										<Grid>
											<Col sm={12} md={8} paddingY="3" paddingLeft="0">
												<Grid gap="4">
													<Col xs={12} sm>
														<SelectField
															select={Select}
															name={`${name}.hotel`}
															label="Hotel"
															options={hotelOptions}
															placeholder="Select hotel..."
															required
															// Changing the hotel invalidates its add-on and nights
															onChange={(hotel: IHotel | undefined) => {
																form.batch(() => {
																	form.change(
																		`${name}.hotel` as never,
																		hotel as never
																	)
																	form.change(
																		`${name}.addon` as never,
																		undefined as never
																	)
																	form.change(
																		`${name}.dates` as never,
																		[] as never
																	)
																	form.change(
																		`${name}.removed_dates` as never,
																		undefined as never
																	)
																})
															}}
														/>
													</Col>
													<GetFieldValue<THotelAddonRow["hotel"]>
														name={`${name}.hotel`}
													>
														{({ value: hotel }) => {
															const nights = getStayNightsForHotel(
																hotels,
																hotel?.id,
																bookingFrom
															)
															return (
																<>
																	<Col xs={12} sm>
																		<SelectField
																			// Remount so options of the previous hotel don't linger
																			key={hotel?.id}
																			select={SelectHotelOtaAddon}
																			name={`${name}.addon`}
																			label="Extra Service"
																			hotelId={hotel?.id}
																			required
																		/>
																	</Col>
																	<Col xs={12} sm>
																		<FieldGroup<THotelAddonRow["dates"]>
																			name={`${name}.dates`}
																			label="Nights"
																		>
																			{({ input }) => (
																				<Select
																					{...input}
																					id={`field_${input.name}`}
																					options={nights}
																					multiple
																					searchable={false}
																					placeholder="Select nights..."
																					required
																					value={nights.filter((n) =>
																						(input.value || []).some((d) =>
																							isSame(d, n.value, "day")
																						)
																					)}
																					onChange={(
																						selected:
																							| Array<TNightOption>
																							| undefined
																					) => {
																						const dates = (selected || []).map(
																							(n) => n.value
																						)
																						dates.sort((a, b) =>
																							isAfter(a, b)
																								? 1
																								: isBefore(a, b)
																									? -1
																									: 0
																						)
																						form.batch(() => {
																							input.onChange(dates)
																							// user picked nights again, notice is no longer relevant
																							form.change(
																								`${name}.removed_dates` as never,
																								undefined as never
																							)
																						})
																					}}
																				/>
																			)}
																		</FieldGroup>
																	</Col>
																</>
															)
														}}
													</GetFieldValue>
													<Col style={{ maxWidth: "120px" }}>
														<TextInputField
															name={`${name}.quantity`}
															label="Quantity"
															type="number"
															min={1}
															placeholder="e.g. 2"
															required
														/>
													</Col>
													<GetFieldValue<THotelAddonRow["hotel"]>
														name={`${name}.hotel`}
													>
														{({ value: hotel }) => (
															<RemovedNightsNotice
																name={name}
																hotel={hotel}
																nights={getStayNightsForHotel(
																	hotels,
																	hotel?.id,
																	bookingFrom
																)}
															/>
														)}
													</GetFieldValue>
												</Grid>
											</Col>
											<Col
												sm={12}
												md={4}
												paddingY="3"
												paddingRight="0"
												borderLeftWidth={{ xs: "0", md: "1" }}
												paddingLeft={{ xs: "0", md: "4" }}
											>
												<HotelAddonPricesInput name={name} />
												<Divider sm />
												<Inline gap="2">
													<Button
														onClick={() => fields.remove(index)}
														level="tertiary"
														size="sm"
													>
														<Icons.Cancel /> Remove
													</Button>
												</Inline>
											</Col>
										</Grid>
									</Box>
								))}
								<Box>
									{hotelOptions.length ? (
										<Button
											onClick={() => {
												fields.push({
													hotel:
														hotelOptions.length === 1
															? hotelOptions[0]
															: undefined,
													addon: undefined,
													dates: [],
													quantity: undefined,
													comments: "",
												})
											}}
										>
											<Icons.Plus /> Add Extra Service
										</Button>
									) : (
										<Text color="muted">
											Add a hotel to this option to add its extra services.
										</Text>
									)}
								</Box>
							</Stack>
						)}
					</FieldArray>
					<SubmitOnChange onChange={notifyOnChange} />
					<SROnly>
						<Button type="submit">Save Extra Services</Button>
					</SROnly>
				</form>
			)}
		</Form>
	)
}

const EMPTY_DATES: Array<Date> = []

// Only per quantity add-ons with a base rate can be priced for now
function isSupportedAddon(addon: THotelAddon) {
	return addon.price_mode === "per_quantity" && addon.rate_before_tax !== null
}

// Rebuilds the nights' prices when nights change (keeping edits), resets everything when add-on changes
function HotelAddonPricesReset({ name }: { name: string }) {
	const form = useForm()
	const { value: addon } = useFieldValue<THotelAddonRow["addon"]>(
		`${name}.addon`
	)
	const { value: dates } = useFieldValue<THotelAddonRow["dates"]>(
		`${name}.dates`
	)
	const { value: date_wise_prices } = useFieldValue<
		THotelAddonRow["date_wise_prices"]
	>(`${name}.date_wise_prices`)
	const previousRef = useRef({
		addonId: addon?.id,
		// on-mount, nights without prices need to be generated
		dates: dates?.length && !date_wise_prices?.length ? EMPTY_DATES : dates,
	})

	useEffect(() => {
		const previous = previousRef.current
		previousRef.current = { addonId: addon?.id, dates }
		const addonChanged = previous.addonId !== addon?.id
		if (!addonChanged && previous.dates === dates) return
		form.change(
			`${name}.date_wise_prices`,
			getHotelAddonDatewisePrices(
				dates || [],
				addonChanged ? undefined : date_wise_prices,
				addon?.currency || "INR"
			)
		)
	}, [addon, dates, date_wise_prices, form, name])
	return null
}

// Same flow as RatesForHotel: debounced, abortable fetch of system rates per night
function RatesForHotelAddon({ name }: { name: string }) {
	const form = useForm()
	const xhr = useXHR()
	const { value: hotel } = useFieldValue<THotelAddonRow["hotel"]>(
		`${name}.hotel`
	)
	const { value: addon } = useFieldValue<THotelAddonRow["addon"]>(
		`${name}.addon`
	)
	const { value: dates } = useFieldValue<THotelAddonRow["dates"]>(
		`${name}.dates`
	)
	const { value: quantity } = useFieldValue<THotelAddonRow["quantity"]>(
		`${name}.quantity`
	)
	const { set: setTimer, clear: clearTimer } = useTimeout()

	useEffect(() => {
		const abortController = new AbortController()
		if (
			!hotel ||
			!addon ||
			!isSupportedAddon(addon) ||
			!Number(quantity) ||
			!dates?.length
		) {
			clearTimer()
			form.change(`${name}.fetching_prices`, 0)
			return () => undefined
		}
		setTimer(() => {
			form.change(`${name}.fetching_prices`, 1)
			hotelAddOnsPricesXHR(xhr)
				.calculatePrices(
					hotel.id,
					addon,
					{
						quantity: Number(quantity),
						dates: dates.map((date) => formatDate(date, "YYYY-MM-DD")),
					},
					{ signal: abortController.signal }
				)
				.then(({ currency, tax_percentage, night_wise_rates }) => {
					form.batch(() => {
						dates.forEach((date, index) => {
							const night = night_wise_rates.find(
								(n) => n.date === formatDate(date, "YYYY-MM-DD")
							)
							const fieldNamePrefix = `${name}.date_wise_prices[${index}]`
							form.change(`${fieldNamePrefix}.date`, date)
							form.change(`${fieldNamePrefix}.currency`, currency)

							form.change(
								`${fieldNamePrefix}.tax_percentage`,
								Number(tax_percentage)
							)
							if (night?.is_available) {
								form.change(
									`${fieldNamePrefix}.calculated_rate_bt`,
									Number(night.rate_per_quantity_bt)
								)
								form.change(
									`${fieldNamePrefix}.price`,
									Number(night.total_amount)
								)
							} else {
								// not sellable on this night, a missing rate shows as N/A
								form.change(`${fieldNamePrefix}.calculated_rate_bt`, undefined)
								form.change(`${fieldNamePrefix}.price`, undefined)
							}
						})
						form.change(`${name}.fetching_prices`, 0)
					})
				})
				.catch((e) => {
					if (!isAbortError(e)) {
						form.change(`${name}.fetching_prices`, 0)
						throw e
					}
				})
		}, 300)
		return () => {
			abortController.abort()
			clearTimer()
		}
	}, [hotel, addon, dates, quantity, form, name, xhr, setTimer, clearTimer])
	return null
}

// Unedited nights follow the system rate; given_price always derives from given rate per quantity + tax × quantity
function HotelAddonGivenPriceSync({ name }: { name: string }) {
	const form = useForm()
	const { value: date_wise_prices } = useFieldValue<
		THotelAddonRow["date_wise_prices"]
	>(`${name}.date_wise_prices`)
	const { value: quantity } = useFieldValue<THotelAddonRow["quantity"]>(
		`${name}.quantity`
	)

	useEffect(() => {
		if (!date_wise_prices?.length) return
		let hasChanges = false
		const synced = date_wise_prices.map((p) => {
			const given_rate_bt = p.edited_given_price
				? p.given_rate_bt
				: p.calculated_rate_bt
			const given_price =
				given_rate_bt === undefined || p.tax_percentage === undefined
					? undefined
					: Number(
							formatMoneyByDecimal(
								getHotelAddonNightPrice({
									rateBt: given_rate_bt,
									taxPercentage: p.tax_percentage,
									quantity: Number(quantity || 0),
									currency: p.currency,
								}).total
							)
						)
			if (given_rate_bt === p.given_rate_bt && given_price === p.given_price) {
				return p
			}
			hasChanges = true
			return { ...p, given_rate_bt, given_price }
		})
		if (hasChanges) form.change(`${name}.date_wise_prices`, synced)
	}, [date_wise_prices, quantity, form, name])
	return null
}

function joinNightLabels(dates: Array<Date>) {
	const labels = dates.map((d) => formatDate(d, "ddd, D MMM"))
	return labels.length > 1
		? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
		: labels[0]
}

// Drops selected nights the hotel's stay no longer covers, and tells the user why
function RemovedNightsNotice({
	name,
	hotel,
	nights,
}: {
	name: string
	hotel?: IHotel
	nights: Array<TNightOption>
}) {
	const form = useForm()
	const { value: dates } = useFieldValue<THotelAddonRow["dates"]>(
		`${name}.dates`
	)
	const { value: removedDates } = useFieldValue<
		THotelAddonRow["removed_dates"]
	>(`${name}.removed_dates`)
	useEffect(() => {
		if (!hotel || !dates?.length) return
		const staleDates = dates.filter(
			(d) => !nights.some((n) => isSame(n.value, d, "day"))
		)
		if (!staleDates.length) return
		form.batch(() => {
			form.change(
				`${name}.dates`,
				dates.filter((d) => !staleDates.includes(d))
			)
			form.change(
				`${name}.removed_dates`,
				(removedDates || [])
					.filter((d) => !staleDates.some((s) => isSame(s, d, "day")))
					.concat(staleDates)
			)
		})
	}, [hotel, dates, nights, removedDates, form, name])

	if (!hotel || !removedDates?.length) return null
	const isSingle = removedDates.length === 1
	return (
		<Col md={12}>
			<Inline gap="1" alignItems="center">
				<Text color="warning" fontSize="sm">
					<Icons.AttentionSolid /> {joinNightLabels(removedDates)}{" "}
					{isSingle ? "was" : "were"} removed from this extra service because{" "}
					{isSingle ? "it's" : "they're"} no longer part of {hotel.name}'s stay.
				</Text>
				<Button
					size="sm"
					level="tertiary"
					title="Dismiss"
					onClick={() => form.change(`${name}.removed_dates`, undefined)}
				>
					<Icons.Cancel />
				</Button>
			</Inline>
		</Col>
	)
}

// Night's system rate excluding tax: rate per quantity × quantity
function getNightRateBeforeTax(
	price: IDaywiseHotelAddonPrice,
	quantity: number
): TMoney | undefined {
	if (price.calculated_rate_bt === undefined) return undefined
	return multipleMoneyBy(
		moneyParseByDecimal(price.calculated_rate_bt, price.currency),
		quantity
	)
}

// Same structure as hotels' PricesInput: header with comments toggle, prices table, comments
function HotelAddonPricesInput({ name }: { name: string }) {
	const { value: comments } = useFieldValue<THotelAddonRow["comments"]>(
		`${name}.comments`
	)
	const [showComments, setShowComments] = useState(
		comments?.length ? true : false
	)
	return (
		<Stack gap="2">
			<Box
				display="flex"
				justifyContent="between"
				alignItems="center"
				height="12"
			>
				<Text fontSize="md" fontWeight="semibold">
					Prices
				</Text>
				{!showComments ? (
					<Button
						level="tertiary"
						size="sm"
						onClick={() => setShowComments(true)}
						title="Add Comments"
					>
						<Icons.Annotation />
					</Button>
				) : null}
			</Box>
			<HotelAddonPricesTable name={name} />
			{showComments ? (
				<TextInputField
					type="text"
					label="Comments"
					name={`${name}.comments`}
					maxLength={191}
					placeholder="Regarding pricing difference or any other"
				/>
			) : null}
		</Stack>
	)
}

function HotelAddonPricesTable({ name }: { name: string }) {
	const form = useForm()
	const { value: date_wise_prices } = useFieldValue<
		THotelAddonRow["date_wise_prices"]
	>(`${name}.date_wise_prices`)
	const { value: quantity } = useFieldValue<THotelAddonRow["quantity"]>(
		`${name}.quantity`
	)
	const { value: fetching_prices } = useFieldValue<
		THotelAddonRow["fetching_prices"]
	>(`${name}.fetching_prices`)
	if (!date_wise_prices?.length || !Number(quantity)) {
		return (
			<Text color="muted" fontSize="sm">
				Select extra service, nights and quantity to see the prices.
			</Text>
		)
	}
	const qty = Number(quantity)
	const ratesBeforeTax = date_wise_prices.map((p) =>
		getNightRateBeforeTax(p, qty)
	)
	return (
		<Table
			bordered
			responsive
			alignCols={{ 1: "center", 3: "center" }}
			headers={[
				"Night",
				<Stack>
					<Inline gap="1" justifyContent="center" alignItems="center">
						<Text>Rate</Text>
						<Text fontSize="sm">
							<MoneySum
								money={addMoneyFromDifferentCurrencies(
									ratesBeforeTax.filter((m): m is TMoney => Boolean(m))
								)}
							/>
						</Text>
						{isTruthy(fetching_prices) ? (
							<Icons.Refresh spin opacity="50" />
						) : null}
					</Inline>
					<Text fontSize="xs">(exc. Tax)</Text>
				</Stack>,
				"", // for sync button
				<Stack>
					<Inline gap="1" justifyContent="center">
						<Text>Given</Text>
						<Text fontSize="sm">
							<MoneySum
								money={addMoneyFromDifferentCurrencies(
									date_wise_prices.map((p) =>
										moneyParseByDecimal(p.given_price || 0, p.currency)
									)
								)}
							/>
						</Text>
					</Inline>
					<Text fontSize="xs">(inc. Tax)</Text>
				</Stack>,
			]}
			rows={date_wise_prices.map(({ date, price, edited_given_price }, j) => [
				<Stack>
					<Box whiteSpace="preserve">{formatDate(date, "D MMM")}</Box>
					<Box whiteSpace="preserve" fontSize="xs" color="muted">
						{formatDate(date, "dddd")}
					</Box>
				</Stack>,
				!ratesBeforeTax[j] ? (
					<Text color="warning" title="Not Available">
						N/A
					</Text>
				) : (
					<Money money={ratesBeforeTax[j]} showCurrency />
				),
				price !== undefined && edited_given_price ? (
					<Button
						size="sm"
						title="Match with Rate"
						level="tertiary"
						onClick={() => {
							form.change(
								`${name}.date_wise_prices.${j}.edited_given_price`,
								false
							)
						}}
					>
						<Icons.ArrowLeft rotate="180" />
					</Button>
				) : null,
				<HotelAddonGivenRateField name={name} index={j} />,
			])}
		/>
	)
}

function HotelAddonGivenRateField({
	name,
	index,
}: {
	name: string
	index: number
}) {
	const form = useForm()
	const [isOpen, open, close] = useDialog()
	const { hasPermission } = useCheckPermissions()
	const { value: date_wise_prices } = useFieldValue<
		THotelAddonRow["date_wise_prices"]
	>(`${name}.date_wise_prices`)
	const { value: quantity } = useFieldValue<THotelAddonRow["quantity"]>(
		`${name}.quantity`
	)
	const { value: addon } = useFieldValue<THotelAddonRow["addon"]>(
		`${name}.addon`
	)
	const nightPrice = date_wise_prices?.[index]
	if (!date_wise_prices || !nightPrice) return null
	const canModifyGivenRate = hasPermission(PERMISSIONS.MODIFY_QUOTE_GIVEN_RATE)
	const invalidGivenPrice =
		nightPrice.given_price === undefined || nightPrice.given_price === null
	return (
		<>
			<Box style={{ width: "100px" }}>
				<Button
					size="sm"
					fullWidth
					onClick={open}
					status={invalidGivenPrice ? "warning" : undefined}
					level={invalidGivenPrice ? "primary" : undefined}
					disabled={!canModifyGivenRate}
				>
					<Inline gap="2" alignItems="center">
						{invalidGivenPrice ? <Icons.AttentionSolid /> : null}
						<Money
							amount={nightPrice.given_price || 0}
							currency={nightPrice.currency}
							showCurrency
						/>
					</Inline>
				</Button>
			</Box>
			<Dialog
				sm
				open={isOpen}
				onClose={close}
				title={`Given Price - ${formatDate(nightPrice.date, "DD MMM")}`}
			>
				<Dialog.Body>
					<HotelAddonGivenRateForm
						addonName={addon?.name || "Extra Service"}
						nightPrice={nightPrice}
						quantity={Number(quantity || 0)}
						hasOtherNights={date_wise_prices.length > 1}
						onCancel={close}
						onSubmit={(givenRateBt, keepSameForOthers) => {
							const indexes = keepSameForOthers
								? Array.from(date_wise_prices.keys())
								: [index]
							form.batch(() => {
								indexes.forEach((i) => {
									const fieldNamePrefix = `${name}.date_wise_prices.${i}`
									form.change(`${fieldNamePrefix}.given_rate_bt`, givenRateBt)
									form.change(
										`${fieldNamePrefix}.edited_given_price`,
										givenRateBt !== date_wise_prices[i].calculated_rate_bt
									)
								})
							})
							close()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function HotelAddonGivenRateForm({
	addonName,
	nightPrice,
	quantity,
	hasOtherNights,
	onSubmit,
	onCancel,
}: {
	addonName: string
	nightPrice: IDaywiseHotelAddonPrice
	quantity: number
	hasOtherNights: boolean
	onSubmit: (givenRateBt: number, keepSameForOthers: boolean) => void
	onCancel: () => void
}) {
	const { currency, tax_percentage, calculated_rate_bt } = nightPrice
	const taxPercentage = Number(tax_percentage || 0)
	const [initialValues] = useState(() => ({
		given_rate_bt: nightPrice.given_rate_bt,
		keep_same_for_other_nights: false,
	}))
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					given_rate_bt: EmptyNumberValidator()
						.required("Please provide the rate")
						.min(0, "Rate can not be negative."),
				})
			)}
			onSubmit={({ given_rate_bt, keep_same_for_other_nights }) => {
				onSubmit(Number(given_rate_bt), Boolean(keep_same_for_other_nights))
			}}
			subscription={{}}
		>
			{({ handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="3">
						<Table
							bordered
							responsive
							hover
							alignCols={{ 2: "center", 3: "center", 4: "right" }}
							headers={["Component", "Rate", "Given", "Qty", "Total"]}
							rows={[
								[
									<Text fontSize="sm" fontWeight="semibold">
										{addonName}
									</Text>,
									<Text fontSize="sm" color="muted">
										{calculated_rate_bt !== undefined ? (
											<Money
												currency={currency}
												amount={calculated_rate_bt}
												showCurrency
											/>
										) : (
											"-"
										)}
									</Text>,
									<TextInputField
										name="given_rate_bt"
										type="number"
										style={{ width: "100px" }}
										min={0}
									/>,
									quantity,
									<GetFieldValue<
										(typeof initialValues)["given_rate_bt"]
									> name="given_rate_bt">
										{({ value }) =>
											value ? (
												<Text>
													<Money
														money={multipleMoneyBy(
															moneyParseByDecimal(Number(value), currency),
															quantity
														)}
														showCurrency
													/>
												</Text>
											) : (
												<Text>-</Text>
											)
										}
									</GetFieldValue>,
								],
							]}
						>
							<tfoot>
								<GetFieldValue<
									(typeof initialValues)["given_rate_bt"]
								> name="given_rate_bt">
									{({ value }) => (
										<tr>
											<TableFooterDataCell colSpan={4}>
												<Text fontWeight="semibold">
													Total (incl. {taxPercentage}% Tax)
												</Text>
											</TableFooterDataCell>
											<TableFooterDataCell textAlign="right">
												<Text fontWeight="semibold">
													<Money
														money={
															getHotelAddonNightPrice({
																rateBt: Number(value || 0),
																taxPercentage,
																quantity,
																currency,
															}).total
														}
														showCurrency
													/>
												</Text>
											</TableFooterDataCell>
										</tr>
									)}
								</GetFieldValue>
							</tfoot>
						</Table>
						{hasOtherNights ? (
							<SwitchInputField
								name="keep_same_for_other_nights"
								label="Apply this rate to all other nights"
							/>
						) : null}
						<Inline gap="2">
							<Button type="submit">Save</Button>
							<Button onClick={onCancel} level="tertiary">
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function SelectHotelOtaAddon({
	hotelId,
	...props
}: Omit<AsyncSelectProps, "fetch"> & { hotelId?: number }) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			fetchOnMount
			disabled={hotelId === undefined}
			placeholder={
				hotelId === undefined
					? "Select a hotel first..."
					: "Select extra service..."
			}
			cacheKey={
				hotelId === undefined ? undefined : `hotel-${hotelId}-ota-addons`
			}
			perFetchLimit={20}
			fetch={(q, { page }) =>
				hotelId === undefined
					? Promise.resolve([])
					: hotelAddOnsXHR(xhr)
							.getAddOns(hotelId, { q, page })
							.then((resp) =>
								resp.data.map((addon) => ({
									...addon,
									disabled: !isSupportedAddon(addon),
								}))
							)
			}
			optionRenderer={({ option }: { option: THotelAddon }) => (
				<Inline gap="4" justifyContent="between" alignItems="baseline">
					<Text>{option.name}</Text>
					<Text fontSize="sm" color="muted" whiteSpace="preserve">
						{isSupportedAddon(option) ? (
							<>
								<Money
									amount={Number(option.rate_before_tax)}
									currency={option.currency}
									showCurrency
								/>{" "}
								+ {Number(option.tax_percentage)}% Tax
							</>
						) : (
							"Pricing not supported yet"
						)}
					</Text>
				</Inline>
			)}
		/>
	)
}

function SubmitOnChange({
	onChange,
}: {
	onChange: (values: IHotelAddonsParams) => void
}) {
	const { values } = useFormState<IHotelAddonsParams>({
		subscription: { values: true },
	})
	const { set: setNotifyParentTimeout, clear: clearNotifyParentTimeout } =
		useTimeout()

	const onChangeRef = useRef(onChange)
	useEffect(() => {
		onChangeRef.current = onChange
	})

	useEffect(() => {
		setNotifyParentTimeout(() => {
			onChangeRef.current(values)
		}, 500)
		return () => clearNotifyParentTimeout()
	}, [values, setNotifyParentTimeout, clearNotifyParentTimeout])
	return null
}
